import { IPoint } from '@src/slide/infrastructure/module/independent/graphic/point/point.interface';

export interface IRect {
    left: number;
    top: number;
    width: number;
    height: number;
}

/**
 * rect之间是否有交叉，包含也被认为是交叉的一种
 * @param rect1
 * @param rect2
 */
export function rectIntersection(rect1: IRect, rect2: IRect): boolean {
    // 判断位置关系
    if (rect1.left > rect2.left + rect2.width
        || rect2.left > rect1.left + rect1.width
        || rect1.top > rect2.top + rect2.height
        || rect2.top > rect1.top + rect1.height) {
        return false;
    }
    // 判断大小关系
    if (rect1.left + rect1.width <= rect1.left
        || rect1.top + rect1.height <= rect1.top
        || rect2.left + rect2.width <= rect2.left
        || rect2.top + rect2.height <= rect2.top) {
        return false;
    }
    return true;
}

/**
 * 判断一个矩形是否在另一个矩形内部
 * @param rect1
 * @param rect2
 */
export function rectInRect(rect1: IRect, rect2: IRect): boolean {
    return rect1.left >= rect2.left && rect1.left + rect1.width <= rect2.left + rect2.width
        && rect1.top >= rect2.top && rect1.top + rect1.height <= rect2.top + rect2.height;
}

/**
 * 矩形的中心点
 * @param rect
 */
export function centerPointOfRect(rect: IRect): IPoint {
    return { x: rect.left + (rect.width / 2), y: rect.top + (rect.height / 2) };
}

/**
 * 判断一个矩形是否完全在另一个矩形的一个内边上
 * @param rect
 * @param otherRect
 */
export function checkRectInOtherRectSide(rect: IRect, otherRect: IRect): boolean {
    let checkNum = 0;
    if (rect.left <= otherRect.left) {
        checkNum += 1;
    }
    if (rect.top <= otherRect.top) {
        checkNum += 1;
    }
    if (rect.left + rect.width >= otherRect.left + otherRect.width) {
        checkNum += 1;
    }
    if (rect.top + rect.height >= otherRect.top + otherRect.height) {
        checkNum += 1;
    }
    if (checkNum === 3) return true;
    return false;
}

/**
 * 判断两个矩形是否完全相同
 * @param rect
 * @param otherRect
 */
export function checkRectSame(rect: IRect, otherRect: IRect): boolean {
    return Math.abs(rect.left - otherRect.left) < 1e-5
        && Math.abs(rect.top - otherRect.top) < 1e-5
        && Math.abs(rect.width - otherRect.width) < 1e-5
        && Math.abs(rect.height - otherRect.height) < 1e-5;
}

// 水平方向上的对齐方式
export enum RectHorizontalAlignType {
    Left,
    Center,
    Right,
    Same, // 水平方向完全相同就直接返回same
}

// 竖直方向上的对齐方式
export enum RectVerticalAlignType {
    Top,
    Center,
    Bottom,
    Same, // 竖直方向完全相同就直接返回same
}

/**
 * 检测两个矩形是否对齐
 * @param rect1
 * @param rect2
 */
// eslint-disable-next-line complexity
export function checkRectAlign(rect1: IRect, rect2: IRect, precision = 1e-3):
    { result: false }
    | {
        result: true,
        horizontalAlignType: RectHorizontalAlignType,
        verticalAlignType: RectVerticalAlignType,
    } {
    const centerPoint1 = centerPointOfRect(rect1);
    const centerPoint2 = centerPointOfRect(rect2);
    let horizontalAlignType;
    let verticalAlignType;
    if (Math.abs(rect1.left - rect2.left) < precision) {
        if (Math.abs(rect1.width - rect2.width) < precision) {
            horizontalAlignType = RectHorizontalAlignType.Same;
        } else {
            horizontalAlignType = RectHorizontalAlignType.Left;
        }
    }
    if (horizontalAlignType === undefined) {
        if (Math.abs(rect1.left + rect1.width - rect2.left - rect2.width) < precision) {
            horizontalAlignType = RectHorizontalAlignType.Right;
        } else if (Math.abs(centerPoint1.x - centerPoint2.x) < precision) {
            horizontalAlignType = RectHorizontalAlignType.Center;
        }
    }
    if (Math.abs(rect1.top - rect2.top) < 1e-3) {
        if (Math.abs(rect1.height - rect2.height) < 1e-3) {
            verticalAlignType = RectHorizontalAlignType.Same;
        } else {
            verticalAlignType = RectVerticalAlignType.Top;
        }
    }
    if (verticalAlignType === undefined) {
        if (Math.abs(rect1.top + rect1.height - rect2.top - rect2.height) < precision) {
            verticalAlignType = RectVerticalAlignType.Bottom;
        } else if (Math.abs(centerPoint1.y - centerPoint2.y) < precision) {
            verticalAlignType = RectVerticalAlignType.Center;
        }
    }
    if (horizontalAlignType !== undefined && verticalAlignType !== undefined) {
        return {
            result: true,
            horizontalAlignType,
            verticalAlignType,
        };
    }
    return {
        result: false,
    };
}

export function checkHorAlignSame(align1: RectHorizontalAlignType, align2: RectHorizontalAlignType): boolean {
    if (align1 === RectHorizontalAlignType.Same || align2 === RectHorizontalAlignType.Same) return true;
    return align1 === align2;
}

export function checkVerAlignSame(align1: RectVerticalAlignType, align2: RectVerticalAlignType): boolean {
    if (align1 === RectVerticalAlignType.Same || align2 === RectVerticalAlignType.Same) return true;
    return align1 === align2;
}
